import React, { useState, useEffect, useRef, useContext } from 'react';
import { StateContext } from '../stateContext';
import apiClient from '../utils/apiClient';
import { useLocation, useParams, useNavigate } from 'react-router-dom';
import { useUser } from '../utils/UserProvider';
import { formatSeconds } from '../utils/timeUtils';
import axios from 'axios';
import storage from "../utils/local_storage";

function ViewScrollingGroupsUpload({ results, uploadID, urls }) {
  const { setToastData } = useContext(StateContext);
  const [imageLoading, setImageLoading] = useState(true);
  const [imageURL, setImageURL] = useState(null);
  const [imageSize, setImageSize] = useState({ width: 0, height: 0 });
  const [imageOffsets, setImageOffsets] = useState({ horizontalOffset: 0, verticalOffset: 0 });
  const imageRef = useRef(null);
  const [scaleFactor, setScaleFactor] = useState({ x: 1, y: 1 });
  
  // Template definition states
  const [isDefiningTemplate, setIsDefiningTemplate] = useState(false);
  const [templateBox, setTemplateBox] = useState(null);
  const [templateFrame, setTemplateFrame] = useState(1);
  const [selectedFrame, setSelectedFrame] = useState(1);
  const [selectedFrameValue, setSelectedFrameValue] = useState(1);
  
  // Drawing states
  const [isDrawing, setIsDrawing] = useState(false);
  const [startPoint, setStartPoint] = useState(null);
  const [currentBox, setCurrentBox] = useState(null);
  
  // Detection parameters
  const [similarityThreshold, setSimilarityThreshold] = useState(75);
  const [similarityValue, setSimilarityValue] = useState(75);
  const [overlapThreshold, setOverlapThreshold] = useState(30);
  const [overlapValue, setOverlapValue] = useState(30);
  const [minGroupSize, setMinGroupSize] = useState(2);
  const [minGroupValue, setMinGroupValue] = useState(2);
  
  // Results
  const [detectedGroups, setDetectedGroups] = useState([]);
  const [selectedGroups, setSelectedGroups] = useState(new Set());
  const [groupNames, setGroupNames] = useState({});
  const [fieldNames, setFieldNames] = useState([]);
  
  // OCR data
  const [ocrResults, setOcrResults] = useState(null);
  const [maxValues, setMaxValues] = useState(null);
  const [isOpen, setIsOpen] = useState(false);
  const [isNoResultsDialogOpen, setIsNoResultsDialogOpen] = useState(false);
  
  const { uploadIDParam } = useParams();
  if (uploadIDParam) uploadID = uploadIDParam;
  const location = useLocation();
  const path = location.pathname;
  const { demoData } = location.state || {};
  const { user } = useUser();
  const navigate = useNavigate();

  useEffect(() => {
    if (imageRef.current) {
      const { offsetWidth: width, offsetHeight: height } = imageRef.current;
      setImageSize({ width, height });
    }
  }, [imageURL]);

  useEffect(() => {
    if (!ocrResults && (path.includes('/demo') || user) && !results) {
      handleUploadOpen(uploadIDParam);
    }
  }, [user, uploadIDParam, path, ocrResults, results]);

  useEffect(() => {
    if (results) {
      setOcrResults(results.ocrResults);
      setMaxValues(results.maxValues);
      setImageURL(urls);
    }
  }, []);

  const handleUploadOpen = async (uploadIDParam) => {
    try {
      console.log("fetching upload data");
      let response;
      
      if (demoData?.results) {
        response = { data: demoData };
      } else {
        if (path.includes('/demo')) uploadIDParam = "demo";
        response = await apiClient.get(`/getUploadDetailsOld/${uploadIDParam}`, {
          params: { mode: 'scrolling' },
        });
      }

      const { uploadData, results, urls } = response.data;
      if (results.results?.length === 0) setIsNoResultsDialogOpen(true);
      
      setImageURL(urls);

      if (results.resultsUrl) {
        const resultsResponse = await axios.get(results.resultsUrl);
        const resultsData = resultsResponse.data;
        setOcrResults(resultsData);
        setMaxValues(resultsData.maxValues);
      } else {
        setOcrResults(results.results);
        setMaxValues(results.maxValues);
      }
    } catch (error) {
      console.error('Error fetching upload details:', error);
      setToastData({ text: "No results found.", show: true });
    }
  };

  const handleImageLoad = () => {
    if (imageRef.current) {
      const { naturalWidth, naturalHeight } = imageRef.current;
      const { width, height } = imageRef.current.getBoundingClientRect();
      
      setScaleFactor({
        x: width / naturalWidth,
        y: height / naturalHeight
      });

      const horizontalOffset = (imageRef.current.parentElement.offsetWidth - width) / 2;
      const verticalOffset = (imageRef.current.parentElement.offsetHeight - height) / 2;
      setImageOffsets({ horizontalOffset, verticalOffset });
    }
  };

  const handleMouseDown = (e) => {
    if (!isDefiningTemplate) return;
    
    const rect = imageRef.current.getBoundingClientRect();
    const x = (e.clientX - rect.left - imageOffsets.horizontalOffset) / scaleFactor.x;
    const y = (e.clientY - rect.top - imageOffsets.verticalOffset) / scaleFactor.y;
    
    setStartPoint({ x, y });
    setIsDrawing(true);
    setCurrentBox(null);
  };

  const handleMouseMove = (e) => {
    if (!isDrawing || !startPoint) return;
    
    const rect = imageRef.current.getBoundingClientRect();
    const x = (e.clientX - rect.left - imageOffsets.horizontalOffset) / scaleFactor.x;
    const y = (e.clientY - rect.top - imageOffsets.verticalOffset) / scaleFactor.y;
    
    setCurrentBox({
      x: Math.min(startPoint.x, x),
      y: Math.min(startPoint.y, y),
      width: Math.abs(x - startPoint.x),
      height: Math.abs(y - startPoint.y)
    });
  };

  const handleMouseUp = () => {
    if (!isDrawing || !currentBox) return;
    
    setTemplateBox(currentBox);
    setTemplateFrame(selectedFrame);
    setIsDrawing(false);
    setIsDefiningTemplate(false);
    setCurrentBox(null);
    
    // Automatically detect groups after template is defined
    detectGroups(currentBox, selectedFrame);
  };

  const detectGroups = (template, frameIdx) => {
    if (!ocrResults || !template) return;
    
    const groups = [];
    const processedBlocks = new Set();
    
    // Detect groups across all frames
    ocrResults.forEach((frameData, frameIndex) => {
      if (!frameData.detections) return;
      
      const frameGroups = [];
      const frameProcessed = new Set();
      
      // Find potential group anchors (blocks that could be the top-left of a group)
      frameData.detections.forEach((block, blockIdx) => {
        if (frameProcessed.has(blockIdx)) return;
        
        const blockCenter = getBlockCenter(block);
        
        // Check if this could be the start of a group similar to template
        const potentialGroup = findGroupAtPosition(
          frameData.detections,
          block,
          template,
          frameProcessed
        );
        
        if (potentialGroup.length >= minGroupSize) {
          frameGroups.push({
            frameIndex,
            blocks: potentialGroup,
            bounds: calculateGroupBounds(potentialGroup)
          });
          
          potentialGroup.forEach(b => frameProcessed.add(b.index));
        }
      });
      
      groups.push(...frameGroups);
    });
    
    // Track groups across frames to identify unique groups
    const trackedGroups = trackGroupsAcrossFrames(groups);
    setDetectedGroups(trackedGroups);
    
    // Initialize field names based on template
    const fields = extractFieldsFromTemplate(template, ocrResults[frameIdx - 1]);
    setFieldNames(fields);
  };

  const findGroupAtPosition = (detections, anchor, template, processed) => {
    const group = [];
    const anchorBounds = getBlockBounds(anchor);
    
    // Find all blocks within the template-sized region starting from anchor
    detections.forEach((block, idx) => {
      if (processed.has(idx)) return;
      
      const blockBounds = getBlockBounds(block);
      const relativePos = {
        x: blockBounds.x - anchorBounds.x,
        y: blockBounds.y - anchorBounds.y
      };
      
      // Check if block falls within template bounds relative to anchor
      if (relativePos.x >= 0 && relativePos.x <= template.width &&
          relativePos.y >= 0 && relativePos.y <= template.height) {
        group.push({
          ...block,
          index: idx,
          relativePosition: relativePos
        });
      }
    });
    
    return group;
  };

  const getBlockBounds = (block) => {
    if (!block.vertices || block.vertices.length < 3) {
      return { x: 0, y: 0, width: 0, height: 0 };
    }
    
    return {
      x: block.vertices[0].x,
      y: block.vertices[0].y,
      width: block.vertices[1].x - block.vertices[0].x,
      height: block.vertices[2].y - block.vertices[0].y
    };
  };

  const getBlockCenter = (block) => {
    const bounds = getBlockBounds(block);
    return {
      x: bounds.x + bounds.width / 2,
      y: bounds.y + bounds.height / 2
    };
  };

  const calculateGroupBounds = (blocks) => {
    if (!blocks.length) return null;
    
    let minX = Infinity, minY = Infinity;
    let maxX = -Infinity, maxY = -Infinity;
    
    blocks.forEach(block => {
      const bounds = getBlockBounds(block);
      minX = Math.min(minX, bounds.x);
      minY = Math.min(minY, bounds.y);
      maxX = Math.max(maxX, bounds.x + bounds.width);
      maxY = Math.max(maxY, bounds.y + bounds.height);
    });
    
    return {
      x: minX,
      y: minY,
      width: maxX - minX,
      height: maxY - minY
    };
  };

  const trackGroupsAcrossFrames = (allGroups) => {
    const trackedGroups = [];
    let groupIdCounter = 0;
    
    // Group by similarity across frames
    allGroups.forEach(group => {
      let matched = false;
      
      for (let tracked of trackedGroups) {
        // Check if this group matches an existing tracked group
        if (calculateGroupSimilarity(group, tracked.representative) > similarityThreshold / 100) {
          tracked.frames.push(group.frameIndex);
          tracked.allBlocks.push(group.blocks);
          matched = true;
          break;
        }
      }
      
      if (!matched) {
        // New group
        trackedGroups.push({
          id: ++groupIdCounter,
          representative: group,
          frames: [group.frameIndex],
          allBlocks: [group.blocks]
        });
      }
    });
    
    return trackedGroups;
  };

  const calculateGroupSimilarity = (groupA, groupB) => {
    // Simple similarity based on bounds overlap and text content
    const boundsA = groupA.bounds;
    const boundsB = groupB.bounds;
    
    if (!boundsA || !boundsB) return 0;
    
    // Calculate overlap
    const xOverlap = Math.max(0, Math.min(boundsA.x + boundsA.width, boundsB.x + boundsB.width) - 
                                 Math.max(boundsA.x, boundsB.x));
    const yOverlap = Math.max(0, Math.min(boundsA.y + boundsA.height, boundsB.y + boundsB.height) - 
                                 Math.max(boundsA.y, boundsB.y));
    
    const overlapArea = xOverlap * yOverlap;
    const areaA = boundsA.width * boundsA.height;
    const areaB = boundsB.width * boundsB.height;
    
    const overlapRatio = overlapArea / Math.min(areaA, areaB);
    
    return overlapRatio;
  };

  const extractFieldsFromTemplate = (template, frameData) => {
    if (!frameData || !frameData.detections) return [];
    
    const fields = [];
    const templateBlocks = [];
    
    // Find blocks within template bounds
    frameData.detections.forEach(block => {
      const bounds = getBlockBounds(block);
      if (bounds.x >= template.x && bounds.x <= template.x + template.width &&
          bounds.y >= template.y && bounds.y <= template.y + template.height) {
        templateBlocks.push({
          ...block,
          relativeX: bounds.x - template.x,
          relativeY: bounds.y - template.y
        });
      }
    });
    
    // Sort by position and create field names
    templateBlocks.sort((a, b) => {
      if (Math.abs(a.relativeY - b.relativeY) < 10) {
        return a.relativeX - b.relativeX;
      }
      return a.relativeY - b.relativeY;
    });
    
    templateBlocks.forEach((block, idx) => {
      fields.push(`Field ${idx + 1}`);
    });
    
    return fields;
  };

  const toggleGroupSelection = (groupId) => {
    const newSelected = new Set(selectedGroups);
    if (newSelected.has(groupId)) {
      newSelected.delete(groupId);
    } else {
      newSelected.add(groupId);
    }
    setSelectedGroups(newSelected);
  };

  const updateGroupName = (groupId, name) => {
    setGroupNames(prev => ({
      ...prev,
      [groupId]: name
    }));
  };

  const updateFieldName = (index, name) => {
    const newFields = [...fieldNames];
    newFields[index] = name;
    setFieldNames(newFields);
  };

  const exportToCSV = () => {
    const headers = ["Group", "Frame", ...fieldNames];
    const rows = [];
    
    detectedGroups.forEach(group => {
      if (!selectedGroups.has(group.id)) return;
      
      const groupName = groupNames[group.id] || `Group ${group.id}`;
      
      group.allBlocks.forEach((frameBlocks, frameIdx) => {
        const row = [groupName, group.frames[frameIdx]];
        
        // Sort blocks by relative position to maintain field order
        const sortedBlocks = [...frameBlocks].sort((a, b) => {
          if (Math.abs(a.relativePosition.y - b.relativePosition.y) < 10) {
            return a.relativePosition.x - b.relativePosition.x;
          }
          return a.relativePosition.y - b.relativePosition.y;
        });
        
        sortedBlocks.forEach(block => {
          row.push(block.text || "");
        });
        
        // Pad with empty strings if fewer fields than expected
        while (row.length < headers.length) {
          row.push("");
        }
        
        rows.push(row);
      });
    });
    
    const csvContent = [headers, ...rows].map(row => 
      row.map(cell => `"${cell}"`).join(",")
    ).join("\n");
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', 'scrolling_groups_export.csv');
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleFrameSelect = (e) => {
    setSelectedFrame(Number(e.target.value));
  };

  const handleFrameValueChange = (e) => {
    setSelectedFrameValue(Number(e.target.value));
    if (urls?.length < 25) {
      setSelectedFrame(Number(e.target.value));
    }
  };

  const reverseFrame = () => {
    const frameNumber = selectedFrame > 1 ? selectedFrame - 1 : selectedFrame;
    setSelectedFrame(frameNumber);
    setSelectedFrameValue(frameNumber);
  };

  const advanceFrame = () => {
    const frameNumber = selectedFrame < ocrResults?.length ? selectedFrame + 1 : selectedFrame;
    setSelectedFrame(frameNumber);
    setSelectedFrameValue(frameNumber);
  };

  return (
    <div className="w-full mx-auto p-4 flex flex-col md:flex-row">
      {isOpen && (
        <div className="modal modal-open">
          <div className="modal-box">
            <h3 className="font-bold text-lg text-center">How to use Scrolling Groups Detection</h3>
            <p className="py-4 text-center">Follow these steps to extract grouped data:</p>
            <ol className="list-decimal pl-5">
              <li>Click "Define Template" and draw a box around one complete group (e.g., one contact card)</li>
              <li>The system will automatically detect similar groups across all frames</li>
              <li>Select the groups you want to export</li>
              <li>Customize group and field names as needed</li>
              <li>Click "Export as CSV" to download your data</li>
            </ol>
            <div className="modal-action">
              <button className="btn" onClick={() => setIsOpen(false)}>Got it!</button>
            </div>
          </div>
        </div>
      )}
      
      {isNoResultsDialogOpen && (
        <div className="modal modal-open">
          <div className="modal-box">
            <h3 className="font-bold text-lg text-center">No Text Results Found</h3>
            <p className="text-center pb-2">Your media does not appear to contain any text.</p>
            <div className="modal-action">
              <button className="btn" onClick={() => navigate("/uploads")}>Back to Uploads</button>
              <button className="btn" onClick={() => setIsNoResultsDialogOpen(false)}>Got it!</button>
            </div>
          </div>
        </div>
      )}
      
      <div className="w-full md:w-1/2 h-full flex flex-col">
        <div className="relative w-full max-h-[50vh] overflow-hidden rounded-lg">
          {imageLoading && !imageURL ? (
            <div className="flex items-center justify-center h-[50vh]">
              <div className="bg-neutral p-4 rounded-lg flex flex-col items-center justify-center space-y-2">
                <span className="loading loading-spinner loading-lg text-white"></span>
                <p className="text-white text-lg">Loading Image</p>
              </div>
            </div>
          ) : (
            <div className="relative"
                 onMouseDown={handleMouseDown}
                 onMouseMove={handleMouseMove}
                 onMouseUp={handleMouseUp}>
              <img
                ref={imageRef}
                src={imageURL?.[selectedFrame - 1]}
                alt="Processed"
                onLoad={() => { handleImageLoad(); setImageLoading(false); }}
                style={{
                  maxWidth: '100%',
                  maxHeight: '50vh',
                  objectFit: 'contain',
                  display: 'block',
                  margin: '0 auto',
                  userSelect: 'none'
                }}
              />
              
              {/* Show template box */}
              {templateBox && templateFrame === selectedFrame && (
                <div
                  className="absolute border-4 border-blue-500 bg-blue-500 bg-opacity-20"
                  style={{
                    left: `${templateBox.x * scaleFactor.x + imageOffsets.horizontalOffset}px`,
                    top: `${templateBox.y * scaleFactor.y + imageOffsets.verticalOffset}px`,
                    width: `${templateBox.width * scaleFactor.x}px`,
                    height: `${templateBox.height * scaleFactor.y}px`,
                  }}
                />
              )}
              
              {/* Show current drawing box */}
              {currentBox && isDrawing && (
                <div
                  className="absolute border-2 border-green-500 bg-green-500 bg-opacity-20"
                  style={{
                    left: `${currentBox.x * scaleFactor.x + imageOffsets.horizontalOffset}px`,
                    top: `${currentBox.y * scaleFactor.y + imageOffsets.verticalOffset}px`,
                    width: `${currentBox.width * scaleFactor.x}px`,
                    height: `${currentBox.height * scaleFactor.y}px`,
                  }}
                />
              )}
              
              {/* Show detected groups on current frame */}
              {detectedGroups.map(group => {
                const groupFrame = group.frames.indexOf(selectedFrame - 1);
                if (groupFrame === -1) return null;
                
                const bounds = calculateGroupBounds(group.allBlocks[groupFrame]);
                if (!bounds) return null;
                
                const isSelected = selectedGroups.has(group.id);
                
                return (
                  <div
                    key={group.id}
                    className={`absolute cursor-pointer ${
                      isSelected ? 'border-2 border-green-500' : 'border-2 border-red-500'
                    } hover:border-dashed`}
                    style={{
                      left: `${bounds.x * scaleFactor.x + imageOffsets.horizontalOffset}px`,
                      top: `${bounds.y * scaleFactor.y + imageOffsets.verticalOffset}px`,
                      width: `${bounds.width * scaleFactor.x}px`,
                      height: `${bounds.height * scaleFactor.y}px`,
                    }}
                    onClick={() => toggleGroupSelection(group.id)}
                  />
                );
              })}
            </div>
          )}
        </div>
        
        <div className="flex flex-col p-2">
          <label>Selected Frame: {selectedFrameValue}</label>
          <div className="flex flex-row items-center gap-2">
            <button className="btn btn-sm" onClick={reverseFrame}>Prev</button>
            <input 
              type="range" 
              className="range range-success" 
              min={1} 
              max={ocrResults?.length || 1} 
              value={selectedFrameValue} 
              onTouchEnd={handleFrameSelect} 
              onMouseUp={handleFrameSelect} 
              onChange={handleFrameValueChange}
            />
            <button className="btn btn-sm" onClick={advanceFrame}>Next</button>
          </div>
        </div>
        
        <div className="w-full mx-auto justify-center items-center border-2 p-2 rounded-lg relative">
          <p className="text-center">Template & Detection Settings</p>
          <button className="btn btn-xs absolute top-2 right-2" onClick={() => setIsOpen(true)}>?</button>
          
          <div className="flex flex-col gap-2 mt-2">
            <button 
              className={`btn ${isDefiningTemplate ? 'btn-warning' : 'btn-primary'}`}
              onClick={() => setIsDefiningTemplate(!isDefiningTemplate)}
            >
              {isDefiningTemplate ? 'Drawing Template...' : 'Define Template'}
            </button>
            
            {templateBox && (
              <div className="text-sm text-center">
                Template defined on frame {templateFrame}
                <button 
                  className="btn btn-xs ml-2"
                  onClick={() => { setTemplateBox(null); setDetectedGroups([]); }}
                >
                  Clear
                </button>
              </div>
            )}
            
            <label>Similarity Threshold: {similarityValue}%</label>
            <input 
              type="range" 
              className="range range-xs" 
              min={50} 
              max={100} 
              value={similarityValue}
              onChange={(e) => setSimilarityValue(Number(e.target.value))}
              onMouseUp={(e) => setSimilarityThreshold(Number(e.target.value))}
              onTouchEnd={(e) => setSimilarityThreshold(Number(e.target.value))}
            />
            
            <label>Minimum Fields per Group: {minGroupValue}</label>
            <input 
              type="range" 
              className="range range-xs" 
              min={1} 
              max={10} 
              value={minGroupValue}
              onChange={(e) => setMinGroupValue(Number(e.target.value))}
              onMouseUp={(e) => setMinGroupSize(Number(e.target.value))}
              onTouchEnd={(e) => setMinGroupSize(Number(e.target.value))}
            />
          </div>
        </div>
      </div>
      
      <div className="w-full md:w-1/2 flex flex-col px-0 md:px-4 pt-4 md:pt-0">
        <div className="border-2 rounded-lg mb-4 max-h-[50vh] overflow-scroll">
          <p className="text-xl text-center font-bold">Detected Groups</p>
          <div className="overflow-auto">
            <table className="table w-full">
              <thead>
                <tr className="bg-base-300 uppercase text-xs">
                  <th className="py-2 px-2 text-center">Select</th>
                  <th className="py-2 px-2 text-center">Group Name</th>
                  <th className="py-2 px-2 text-center">Frames</th>
                  <th className="py-2 px-2 text-center">Fields</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                {detectedGroups.length > 0 ? (
                  detectedGroups.map(group => (
                    <tr key={group.id} className="hover:bg-base-300">
                      <td className="py-2 px-2 text-center">
                        <input
                          type="checkbox"
                          className="checkbox"
                          checked={selectedGroups.has(group.id)}
                          onChange={() => toggleGroupSelection(group.id)}
                        />
                      </td>
                      <td className="py-2 px-2 text-center">
                        <input
                          type="text"
                          className="input input-sm input-bordered w-full"
                          value={groupNames[group.id] || `Group ${group.id}`}
                          onChange={(e) => updateGroupName(group.id, e.target.value)}
                        />
                      </td>
                      <td className="py-2 px-2 text-center">
                        {group.frames.length}
                      </td>
                      <td className="py-2 px-2 text-center">
                        {group.allBlocks[0]?.length || 0}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="4" className="text-center py-4">
                      {templateBox ? 'Processing...' : 'Define a template to detect groups'}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
        
        {fieldNames.length > 0 && (
          <div className="border-2 rounded-lg mb-4 p-4">
            <p className="text-lg text-center font-bold mb-2">Field Names</p>
            <div className="grid grid-cols-2 gap-2">
              {fieldNames.map((name, idx) => (
                <div key={idx} className="flex items-center gap-2">
                  <label className="text-sm">Field {idx + 1}:</label>
                  <input
                    type="text"
                    className="input input-sm input-bordered flex-1"
                    value={name}
                    onChange={(e) => updateFieldName(idx, e.target.value)}
                  />
                </div>
              ))}
            </div>
          </div>
        )}
        
        {selectedGroups.size > 0 && (
          <div className="flex flex-row gap-4 items-center justify-center mx-auto pt-4">
            <button className="btn btn-success" onClick={exportToCSV}>
              Export as CSV ({selectedGroups.size} groups)
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default ViewScrollingGroupsUpload;